import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, FileWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/utils/formatDate';
import type {
  DebugSessionListItem,
  PaginatedDebugSessionsResponse,
} from '@/types/superUserDebug.ts';

interface Props {
  data: PaginatedDebugSessionsResponse | null;
  isLoading: boolean;
  error: string | null;
  onPageChange: (page: number) => void;
}

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

export default function SuperUserDebugSessionsTable({
  data,
  isLoading,
  error,
  onPageChange,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <FileWarning className="size-4" />
        {error}
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
        {t('superuser.debug.table.loading', 'Cargando sesiones…')}
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
        {t('superuser.debug.table.empty', 'No hay sesiones que coincidan con los filtros.')}
      </div>
    );
  }

  const start = (data.page - 1) * data.pageSize + 1;
  const end = Math.min(data.page * data.pageSize, data.totalCount);

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">
                {t('superuser.debug.table.col.startedAt', 'Fecha')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('superuser.debug.table.col.salesperson', 'Usuario')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('superuser.debug.table.col.company', 'Compañía')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('superuser.debug.table.col.simulation', 'Simulación')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <Row key={s.id} session={s} onClick={() => navigate(`/superuser/debug/sessions/${s.id}`)} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {t('superuser.debug.table.range', '{{start}}–{{end}} de {{total}}', {
            start,
            end,
            total: data.totalCount,
          })}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={data.page <= 1}
            onClick={() => onPageChange(data.page - 1)}
          >
            <ChevronLeft className="mr-1 size-4" />
            {t('superuser.debug.table.prev', 'Anterior')}
          </Button>
          <span className="text-xs text-muted-foreground">
            {t('superuser.debug.table.pageOf', 'Pág. {{page}}/{{total}}', {
              page: data.page,
              total: Math.max(data.totalPages, 1),
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={data.page >= data.totalPages}
            onClick={() => onPageChange(data.page + 1)}
          >
            {t('superuser.debug.table.next', 'Siguiente')}
            <ChevronRight className="ml-1 size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ session, onClick }: { session: DebugSessionListItem; onClick: () => void }) {
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
    >
      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
        {formatDate(session.startedAt, DATE_OPTS)}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col">
          <span className="font-medium">{session.salespersonFullName || session.salespersonEmail}</span>
          <span className="text-xs text-muted-foreground">{session.salespersonEmail}</span>
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {session.salespersonCompany || '—'}
      </td>
      <td className="px-3 py-2">{session.simulationName ?? '—'}</td>
    </tr>
  );
}
